import { useEffect, useState } from "react";
import LoadField from "./LoadField";
import SetGuidanceRow from "./SetGuidanceRow";
import { usableLastReps } from "../domain/previousPerformance";
import { renderSuggested, suggestedContextForSet } from "../domain/suggestedDisplay";
import RepsField from "./RepsField";
import ExecutionRirControl from "./ExecutionRirControl";
import EntryHintInfo from "./EntryHintInfo";
import {
  defaultUnitForSet,
  isSetPerformed,
  loadPresentation,
  prescribedRepsGhost,
} from "../domain/executionUi";

export default function SetEntryRow({
  item,
  set,
  prescribed,
  profile,
  preferences,
  rirEnabled,
  disabled,
  onPatch,
  onRemove,
  compact = false,
  ordinal = null,
  unitMemory = null,
  // Previous performance (§13.10): this set's mapped LAST, and the item's
  // Suggested on the first working set only. Both are null when the feature
  // is OFF or there is no history, and the row is then exactly the baseline.
  last = null,
  suggested = null,
  // False once the workout is FINISHED: LAST stays visible, nothing is seeded.
  seedFromLast = true,
  onFieldUnitChange = null,
}) {
  // The visible number is the row's current 1-based place in the item.
  // `setIndex` stays the persisted structural index (never renumbered, so
  // it keeps gaps after a removed set) and is not shown to the athlete.
  const setNumber = ordinal ?? set.setIndex;
  const performed = isSetPerformed(profile, set, rirEnabled);
  const defaultUnit = defaultUnitForSet(item, set, preferences, unitMemory);
  // The unit the athlete currently sees on the field, including a relabel
  // made before any value is entered.
  const [fieldUnit, setFieldUnit] = useState(null);
  useEffect(() => {
    onFieldUnitChange?.(fieldUnit);
  }, [onFieldUnitChange, fieldUnit]);
  const renderedSuggested = suggested
    ? renderSuggested(suggested, suggestedContextForSet({
        profile,
        preferences,
        set,
        defaultUnit: fieldUnit || defaultUnit,
      }))
    : null;
  return (
    <div
      className={`lz-workout-set-row${compact ? " lz-workout-set-row--compact" : ""}${performed ? " is-performed" : ""}`}
      data-set-index={set.setIndex}
      data-set-ordinal={setNumber}
      data-performed={performed ? "true" : "false"}
    >
      <span className="lz-workout-set-number">
        {compact ? (
          <span className="lz-workout-set-state" aria-hidden="true">
            {performed ? "✓" : setNumber}
          </span>
        ) : null}
        {compact ? <span className="lz-v2-visually-hidden">Set </span> : null}
        {compact && performed ? <span>{setNumber}</span> : !compact ? setNumber : null}
        {performed ? <span className="lz-v2-visually-hidden"> completed</span> : null}
      </span>
      <LoadField
        profile={profile}
        load={set.load}
        defaultUnit={defaultUnit}
        preferences={preferences}
        setIndex={setNumber}
        disabled={disabled}
        compact={compact}
        lastSet={seedFromLast ? last : null}
        onUnitChange={setFieldUnit}
        onCommit={(load) => onPatch({ load })}
      />
      <RepsField
        value={set.reps}
        placeholder={prescribedRepsGhost(prescribed)}
        setIndex={setNumber}
        disabled={disabled}
        compact={compact}
        seedValue={seedFromLast ? usableLastReps(last) : null}
        onCommit={(reps) => onPatch({ reps })}
      />
      {rirEnabled ? (
        <ExecutionRirControl
          value={set.rir}
          targetRir={prescribed?.targetRir}
          setIndex={setNumber}
          disabled={disabled}
          compact={compact}
          onCommit={(rir) => onPatch({ rir })}
        />
      ) : null}
      {set.origin === "ADDED" ? (
        <button type="button" className="lz-workout-icon-button" disabled={disabled} onClick={onRemove} aria-label={`Remove set ${setNumber}`}>×</button>
      ) : null}
      {!compact ? (
        <EntryHintInfo>{loadPresentation(profile, preferences, set.load, fieldUnit || defaultUnit).info}</EntryHintInfo>
      ) : null}
      <SetGuidanceRow
        measurementType="REPS"
        last={last}
        suggested={renderedSuggested}
        targetRir={prescribed?.targetRir}
        disabled={disabled}
        onFill={(load) => onPatch({ load })}
      />
    </div>
  );
}
